// Membership payment history stored in profile lastRenewalReference as JSON:
// { "history": [ { "reference", "amount", "userTypeId" }, ... ] }
//
// Legacy profiles may still have a plain reference string.

const MEMBERSHIP_REFERENCE_PREFIXES = ['Renew_', 'SignUp_'];

const normalizeLegacyReference = (reference) => {
  const trimmed = String(reference || '').trim();
  if (!trimmed) return '';

  const hasKnownPrefix = MEMBERSHIP_REFERENCE_PREFIXES.some((prefix) => trimmed.startsWith(prefix));
  return hasKnownPrefix ? trimmed : `Renew_${trimmed}`;
};

const isHistoryContainer = (value) => (
  value
  && typeof value === 'object'
  && !Array.isArray(value)
  && Array.isArray(value.history)
);

const sanitizeHistoryEntry = (entry) => {
  if (!entry || typeof entry !== 'object') return null;

  const reference = String(entry.reference || '').trim();
  if (!reference) return null;

  const amount = Number(entry.amount);
  const userTypeId = entry.userTypeId != null ? String(entry.userTypeId).trim() : '';

  return {
    reference,
    amount: Number.isFinite(amount) && amount >= 0 ? amount : 0,
    userTypeId
  };
};

export const parseMembershipPaymentHistory = (rawValue, migrationFallback = null) => {
  if (rawValue == null || rawValue === '') {
    return { history: [], previousRaw: null };
  }

  if (typeof rawValue === 'object' && !Array.isArray(rawValue)) {
    if (isHistoryContainer(rawValue)) {
      const history = rawValue.history
        .map(sanitizeHistoryEntry)
        .filter(Boolean);
      return { history, previousRaw: JSON.stringify({ history }) };
    }
  }

  const rawString = String(rawValue).trim();
  if (!rawString) {
    return { history: [], previousRaw: null };
  }

  try {
    const parsed = JSON.parse(rawString);
    if (isHistoryContainer(parsed)) {
      const history = parsed.history
        .map(sanitizeHistoryEntry)
        .filter(Boolean);
      return { history, previousRaw: rawString };
    }
  } catch {
    // Fall through to legacy string handling.
  }

  const fallbackAmount = Number(migrationFallback?.amount);
  const fallbackUserTypeId = migrationFallback?.userTypeId != null
    ? String(migrationFallback.userTypeId).trim()
    : '';

  const legacyEntry = sanitizeHistoryEntry({
    reference: normalizeLegacyReference(rawString),
    amount: Number.isFinite(fallbackAmount) && fallbackAmount >= 0 ? fallbackAmount : 0,
    userTypeId: fallbackUserTypeId
  });

  return {
    history: legacyEntry ? [legacyEntry] : [],
    previousRaw: rawString
  };
};

const parseDdMmYyyy = (dateStr) => {
  const parts = String(dateStr || '').split('-');
  if (parts.length !== 3) return null;
  const day = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10);
  const year = parseInt(parts[2], 10);
  if (!day || !month || !year) return null;
  const date = new Date(year, month - 1, day);
  date.setHours(0, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const parseMembershipReferenceDate = (reference) => {
  const ref = String(reference || '').trim();
  if (!ref) return null;

  const timestampMatch = ref.match(/_(\d{2}-\d{2}-\d{4})-\d{2}:\d{2}:\d{2}$/);
  if (timestampMatch) {
    return parseDdMmYyyy(timestampMatch[1]);
  }

  const dateMatch = ref.match(/(\d{2})-(\d{2})-(\d{4})$/);
  if (dateMatch) {
    return parseDdMmYyyy(`${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`);
  }

  return null;
};

export const isSignUpReference = (reference) => (
  String(reference || '').trim().startsWith('SignUp_')
);

export const isRenewReference = (reference) => {
  const ref = String(reference || '').trim();
  return ref.startsWith('Renew_') || (!ref.startsWith('SignUp_') && ref.length > 0);
};

export const usersHaveMembershipPaymentData = (users = []) => (
  users.some((user) => {
    if (!user?.lastRenewalReference) return false;
    const { history } = parseMembershipPaymentHistory(user.lastRenewalReference, {
      userTypeId: user.userType
    });
    return history.length > 0;
  })
);

export const collectMembershipPaymentsInRange = ({
  users = [],
  start,
  end,
  selectedUserTypeIds = null,
  userTypesLoaded = false
}) => {
  const startDate = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endDate = new Date(end.getFullYear(), end.getMonth(), end.getDate());
  const payments = [];

  for (const user of users) {
    if (!user?.lastRenewalReference) continue;

    const { history } = parseMembershipPaymentHistory(user.lastRenewalReference, {
      userTypeId: user.userType
    });

    for (const entry of history) {
      const paymentDate = parseMembershipReferenceDate(entry.reference);
      if (!paymentDate) continue;
      if (paymentDate < startDate || paymentDate > endDate) continue;

      const userTypeId = entry.userTypeId || String(user.userType || '');
      if (userTypesLoaded && selectedUserTypeIds != null) {
        if (selectedUserTypeIds.size === 0) continue;
        if (userTypeId && !selectedUserTypeIds.has(userTypeId)) continue;
      }

      payments.push({
        ...entry,
        userTypeId,
        paymentDate,
        isSignUp: isSignUpReference(entry.reference)
      });
    }
  }

  return payments;
};
